import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';

import { useApp } from 'contexts/AppContext';
import { useUser } from 'contexts/UserContext';

import {
  ACTIVATION,
  CONNEXION,
  CUSTOMERS,
  DATA_PROVIDERS,
  DOMAINCONFIG,
  GEOINTERFACE,
  IMPORTMODEL,
  USERPROFILE
} from 'utils/rest';

import { Loading } from 'components/Loading';
import { UnauthorizedModal } from 'components/UnauthorizedModal';

import { DomainForm, DomainSetup } from './components';

function DomainContainer() {
  const { domainConfig = {}, resfreshDomainConfig } = useApp();
  const { adminRights, user, fetchUser } = useUser();

  const [dataProviders, setDataProviders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [shopGroups, setShopGroups] = useState([]);
  const [presetList, setPresetList] = useState([]);
  const [connectorsConnexions, setConnectorsConnexions] = useState([]);
  const [importModels, setImportModels] = useState([]);
  const [activableMedias, setActivableMedias] = useState([]);
  const [geoInterfaces, setGeoInterfaces] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [formMode, setFormMode] = useState('edit');
  const [formVersion, setFormVersion] = useState(0);
  const [openModal, setModal] = useState(null);

  const hasCurrentDomainConfig = !!domainConfig?._id;
  const isCreateMode = formMode === 'create' || !hasCurrentDomainConfig;

  const closeModal = () => {
    setModal(null);
  };

  const normalizeDataProviders = providers => {
    return providers.map(provider => ({
      key: provider,
      label: provider
    }));
  };

  const getDataProviders = async () => {
    const response = await DATA_PROVIDERS.get();
    return normalizeDataProviders(response);
  };

  const getPresetList = async () => {
    try {
      const result = await ACTIVATION.getActivationPresets();
      return result;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const getDatas = async () => {
    const [
      fetchedProviders,
      presets,
      allActivableMedias,
      allGeoInterfaces,
      connexions,
      resImportModels,
      allCustomers,
      allShopGroups,
      allProfiles
    ] = await Promise.all([
      getDataProviders(),
      getPresetList(),
      ACTIVATION.getActivableMedias(),
      GEOINTERFACE.get(),
      CONNEXION.getConnexions(),
      IMPORTMODEL.get(),
      CUSTOMERS.get(),
      ACTIVATION.getActivationShopGoups(),
      USERPROFILE.get()
    ]);

    setGeoInterfaces(allGeoInterfaces);
    setDataProviders(fetchedProviders);
    setCustomers(allCustomers);
    setActivableMedias(allActivableMedias);
    setPresetList(presets);
    setShopGroups(allShopGroups);
    setProfiles(allProfiles);
    setImportModels(
      resImportModels.map(e => ({
        label: e.label,
        value: e.model
      }))
    );
    setConnectorsConnexions(
      connexions.map(e => ({
        label: e.name,
        value: e._id,
        model: e.model
      }))
    );
    setLoaded(true);
  };

  useEffect(() => {
    if (!loaded) {
      getDatas();
    }
    // eslint-disable-next-line
  }, [loaded]);

  const handleSetupDomain = async data => {
    if (!adminRights?.general?.domain?.create) {
      setModal('unauthorized');
      return;
    }

    try {
      const res = await DOMAINCONFIG.create(data);
      if (res) {
        setFormMode('edit');
        setLoaded(false);
        await fetchUser();
      }
    } catch (err) {
      console.error(err.message);
      throw err;
    }
  };

  const handleUpdateDomainConfig = async data => {
    if (!adminRights?.general?.domain?.update) {
      setModal('unauthorized');
      return;
    }

    try {
      const apis = [];
      if (data.autoLogin !== undefined) {
        apis.push(DOMAINCONFIG.updateAutoLogin(data.domain, data.autoLogin));
      }
      apis.push(
        DOMAINCONFIG.update(domainConfig._id, {
          ...data,
          domain: user.domain
        })
      );

      await Promise.all(apis);

      await resfreshDomainConfig();
      setLoaded(false);
      setFormVersion(prev => prev + 1);
    } catch (err) {
      console.error(err.message);
      throw err;
    }
  };

  const createDomainConfig = () => {
    if (adminRights?.general?.domain?.create) {
      setFormMode('create');
      setFormVersion(prev => prev + 1);
    } else {
      setModal('unauthorized');
    }
  };

  const cancelForm = () => {
    if (isCreateMode && hasCurrentDomainConfig) {
      setFormMode('edit');
    }
    setFormVersion(prev => prev + 1);
  };

  const getProfileKeys = () => {
    return profiles.reduce((acc, el) => {
      if (el.viewKey === 'pml') {
        acc.push({ key: el.key, label: el.name });
      }
      return acc;
    }, []);
  };

  const formTitle = isCreateMode
    ? 'Configuration du Domaine : Nouveau domaine'
    : `Configuration du Domaine : ${domainConfig?.name || user?.domain}`;

  return (
    <Box
      style={{
        width: '100%',
        maxWidth: 'calc(100vw - 410px - 16px)',
        minWidth: 0,
        boxSizing: 'border-box',
        overflowX: 'clip'
      }}
    >
      <Loading loading={!loaded}>
        {isCreateMode ? (
          <DomainSetup
            key={`domain-create-${formVersion}`}
            actionName="Créer"
            saveLabel="Créer le domaine"
            title={formTitle}
            onClose={cancelForm}
            onValidate={handleSetupDomain}
            dataProviders={dataProviders}
            presetList={presetList}
            activableMedias={activableMedias}
            geoInterfaces={geoInterfaces}
          />
        ) : (
          <DomainForm
            key={`domain-edit-${formVersion}-${domainConfig?._id}`}
            actionName="Mettre à jour"
            saveLabel="Mettre à jour le domaine"
            title={formTitle}
            onClose={cancelForm}
            onCreate={createDomainConfig}
            onValidate={handleUpdateDomainConfig}
            config={domainConfig}
            dataProviders={dataProviders}
            preset={presetList}
            connectorsConnexions={connectorsConnexions}
            importModels={importModels}
            activableMedias={activableMedias}
            geoInterfaces={geoInterfaces}
            customers={customers}
            shopGroups={shopGroups}
            profileKeys={getProfileKeys()}
          />
        )}
      </Loading>
      {openModal === 'unauthorized' && (
        <UnauthorizedModal
          open={openModal === 'unauthorized'}
          onClose={closeModal}
        />
      )}
    </Box>
  );
}

export default DomainContainer;
